import HeaderTable from "./components/HeaderTable";
import { formatMoney } from "infra/helpers/number";
import {
  RelatorioDemonstrativoReceitaDespesaResponse,
  FiltroRelatorioReceitaDespesa,
} from "features/relatorios/typings";
import moment from "moment";
import CardDefault from "components/Cards/CardDefault";
import RelatorioSectionTitle from "ui/components/Relatorio/RelatorioSectionTitle";
import RelatorioItem from "./components/RelatorioItem";
import { OperacaoEnum } from "resources/enums/conta";
import colors from "resources/theme/colors";
import { Empty } from "antd";
import { useLoading } from "components/GlobalLoading";
import { DemonstrativoReceitaEDespesaItem } from "core/features/relatorios/typings";
import { Text, Flex } from "@chakra-ui/react";
import { flattenRelatorioCategoriasTree } from "core/features/relatorios/utils/flattenRelatorioCategoriasTree";
import { useLocalConfig } from "core/features/configuracao-local/store";

interface RelatorioDemonstrativoProps {
  onChangeFiltro: (v: Partial<FiltroRelatorioReceitaDespesa>) => void;
  filtro: FiltroRelatorioReceitaDespesa;
  data: RelatorioDemonstrativoReceitaDespesaResponse | undefined;
  isLoading?: boolean;
  setItemDetalhamentoContaCategoria: (
    item: DemonstrativoReceitaEDespesaItem,
  ) => void;
}

function RelatorioDemonstrativo({
  filtro,
  data,
  isLoading,
  onChangeFiltro,
  setItemDetalhamentoContaCategoria,
}: RelatorioDemonstrativoProps) {
  const flattenReceitas = flattenRelatorioCategoriasTree(data?.receitas || []);
  const flattenDespesas = flattenRelatorioCategoriasTree(data?.despesas || []);
  const { config, update } = useLocalConfig();

  useLoading(!!isLoading);
  const relatorioSectionTitleItens = [
    {
      text: `Saldo em ${moment(filtro.dataInicio)
        .add(-1, "days")
        .format("DD/MM/YYYY")}${
        moment(filtro.dataInicio).isAfter(moment().endOf("day"))
          ? " (Previsto)"
          : ""
      }`,
      colSpan: 16,
      bold: true,
    },

    {
      text: `${formatMoney(data?.totais.saldoInicial || 0)}`,
      colSpan: 8,
      bold: true,
      textAlign: "right",
      color: colors.primary,
    },
  ];
  if (typeof data?.totais.totalCentroDeCustoInicioPeriodo === "number") {
    relatorioSectionTitleItens.push(
      {
        text: `Saldo no(s) centro(s) de custo em  ${moment(filtro.dataInicio)
          .add(-1, "days")
          .format("DD/MM/YYYY")}${
          moment(filtro.dataInicio).isAfter(moment().endOf("day"))
            ? " (Previsto)"
            : ""
        }`,
        colSpan: 16,
        bold: true,
      },
      {
        text: `${formatMoney(data?.totais.totalCentroDeCustoInicioPeriodo || 0)}`,
        colSpan: 8,
        bold: true,
        textAlign: "right",
        color: colors.primary,
      },
    );
  }
  return (
    <CardDefault>
      <HeaderTable
        value={filtro.search || ""}
        onSearch={(v) => onChangeFiltro({ search: v })}
        onSelectOperacao={(operacao) => onChangeFiltro({ operacao })}
        operacao={filtro.operacao}
        filtro={filtro}
        receitas={flattenReceitas}
        despesas={flattenDespesas}
        transferencias={data?.transferencias}
        totais={data?.totais!}
        somarSubcategorias={config.SomarSubcategoriasRelatorioDemonstrativo}
        setSomarSubcategorias={(v) =>
          update("SomarSubcategoriasRelatorioDemonstrativo", v)
        }
      />

      {filtro.dataInicio ? (
        <RelatorioSectionTitle
          containerProps={{ mt: 8, mb: 4 }}
          // @ts-ignore
          items={relatorioSectionTitleItens}
        />
      ) : null}

      {filtro.operacao === OperacaoEnum.Receita || !filtro.operacao ? (
        <>
          <RelatorioSectionTitle
            containerProps={{ mt: 4, mb: 4 }}
            items={[
              {
                text: "Receita",
                colSpan: 16,
                bold: true,
              },
              {
                text: "Porcentagem (%)",
                colSpan: 4,
                bold: true,
              },
              {
                text: "Valor",
                colSpan: 4,
                textAlign: "right",
                bold: true,
              },
            ]}
          />

          {flattenReceitas.map((receita, index) => (
            <RelatorioItem
              key={`${receita.nome}${index}`}
              item={receita}
              index={index}
              indent={receita.nivel}
              startBg="white"
              onClick={() => {
                setItemDetalhamentoContaCategoria(receita);
              }}
              somarSubcategorias={
                config.SomarSubcategoriasRelatorioDemonstrativo
              }
            />
          ))}

          {!isLoading && !flattenReceitas.length ? (
            <Empty description="Sem contas categorias de receita para o filtro atual" />
          ) : null}
        </>
      ) : null}

      {filtro.operacao === OperacaoEnum.Despesa || !filtro.operacao ? (
        <>
          <RelatorioSectionTitle
            containerProps={{ mt: 8, mb: 4 }}
            items={[
              {
                text: "Despesas",
                colSpan: 16,
                bold: true,
              },
              {
                text: "Porcentagem (%)",
                colSpan: 4,
                bold: true,
              },
              {
                text: "Valor",
                colSpan: 4,
                textAlign: "right",
                bold: true,
              },
            ]}
          />

          {flattenDespesas.map((despesa, index) => (
            <RelatorioItem
              key={`${despesa.nome}${index}`}
              item={despesa}
              index={index}
              indent={despesa.nivel}
              startBg="white"
              onClick={() => {
                setItemDetalhamentoContaCategoria(despesa);
              }}
            />
          ))}

          {!isLoading && !flattenDespesas.length ? (
            <Empty description="Sem contas categorias de despesa para o filtro atual" />
          ) : null}
        </>
      ) : null}

      {data?.transferencias?.length ? (
        <>
          <RelatorioSectionTitle
            containerProps={{ mt: 8, mb: 1 }}
            items={[
              {
                text: `Transferências`,
                colSpan: 16,
                bold: true,
              },
              {
                text: `Valor`,
                colSpan: 8,
                textAlign: "right",
                bold: true,
              },
            ]}
          />

          {data?.transferencias.map((transferencia) => (
            <Flex mt={4} key={transferencia.id}>
              <Text
                color={colors.black600}
                display="inline"
                fontWeight="medium"
              >
                {moment(transferencia.data).format("DD/MM/YYYY")} - De{" "}
                <Text
                  fontWeight="medium"
                  display="inline"
                  textDecoration="underline"
                >
                  {transferencia.contaOrigem}
                </Text>{" "}
                para{" "}
                <Text
                  fontWeight="medium"
                  display="inline"
                  textDecoration="underline"
                >
                  {transferencia.contaDestino}
                </Text>
              </Text>
              <Text display="inline" ml="auto">
                {formatMoney(transferencia.valor)}
              </Text>
            </Flex>
          ))}
        </>
      ) : null}

      <RelatorioSectionTitle
        containerProps={{ mt: 8, mb: 1 }}
        items={[
          {
            text: `Resultado (Receitas - Despesas)`,
            colSpan: 20,
            bold: true,
          },
          {
            text: `${formatMoney(data?.totais.resultado || 0)}`,
            colSpan: 4,
            bold: true,
            textAlign: "right",
            color: (data?.totais.resultado || 0) < 0 ? colors.red : undefined,
          },
        ]}
      />
    </CardDefault>
  );
}

export default RelatorioDemonstrativo;
